import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  CubeIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import { fetchKnownApps, addKnownApp, removeKnownApp } from '../api/client';
import Pagination from './Pagination';
import Badge from './ui/Badge';
import { appCategoryTone } from './ui/badgeTones';

const CATEGORIES = ['Microsoft', 'ThirdParty', 'Custom'];

function formatDate(dateStr) {
  if (!dateStr) return '\u2014';
  try {
    return format(parseISO(dateStr), 'MMM d, yyyy');
  } catch {
    return dateStr;
  }
}

export default function KnownAppManager() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterQuery, setFilterQuery] = useState('');

  // Scope filter (client-side): ALL, GLOBAL, TENANT
  const [scope, setScope] = useState('ALL');

  // Add form state
  const [appId, setAppId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [formCategory, setFormCategory] = useState('Custom');
  const [formTenantId, setFormTenantId] = useState('GLOBAL');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);

  // Remove confirmation
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [removing, setRemoving] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Resizable columns: App ID, Display Name, Category, Scope, Added By, Date, Actions
  const [colWidths, setColWidths] = useState([280, 280, 100, 200, 180, 120, 100]);
  const thRefs = useRef([]);
  const tableRef = useRef(null);

  const startResize = useCallback((e, colIdx) => {
    e.preventDefault();
    const th = thRefs.current[colIdx];
    const table = tableRef.current;
    if (!th) return;
    const startX = e.clientX;
    const startWidth = th.offsetWidth;
    const startTableWidth = table ? table.offsetWidth : 0;
    let lastWidth = startWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMouseMove = (moveEvt) => {
      lastWidth = Math.max(50, startWidth + moveEvt.clientX - startX);
      th.style.width = `${lastWidth}px`;
      if (table) table.style.width = `${startTableWidth + (lastWidth - startWidth)}px`;
    };
    const onMouseUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      setColWidths(prev => { const n = [...prev]; n[colIdx] = lastWidth; return n; });
      if (table) table.style.width = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, []);

  const loadApps = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchKnownApps('_ALL');
      setApps(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load known apps');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadApps();
  }, []);

  const filteredApps = useMemo(() => {
    let result = apps;
    // Apply scope filter
    if (scope === 'GLOBAL') {
      result = result.filter((app) => app.tenantId === 'GLOBAL');
    } else if (scope === 'TENANT') {
      result = result.filter((app) => app.tenantId && app.tenantId !== 'GLOBAL');
    }
    // Apply text filter
    if (filterQuery.trim()) {
      const q = filterQuery.toLowerCase();
      result = result.filter(
        (app) =>
          (app.appId && app.appId.toLowerCase().includes(q)) ||
          (app.displayName && app.displayName.toLowerCase().includes(q)) ||
          (app.category && app.category.toLowerCase().includes(q)) ||
          (app.addedBy && app.addedBy.toLowerCase().includes(q))
      );
    }
    return result;
  }, [apps, filterQuery, scope]);

  // Reset to page 1 when filter or scope changes
  useEffect(() => { setCurrentPage(1); }, [filterQuery, scope]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredApps.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIdx = (safeCurrentPage - 1) * pageSize;
  const pagedApps = filteredApps.slice(startIdx, startIdx + pageSize);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!appId.trim() || !displayName.trim()) return;

    setAdding(true);
    setAddError(null);
    try {
      const newApp = await addKnownApp({
        appId: appId.trim(),
        displayName: displayName.trim(),
        category: formCategory,
        tenantId: formTenantId.trim() || 'GLOBAL',
      });
      setApps((prev) => [...prev, newApp]);
      setAppId('');
      setDisplayName('');
      setFormCategory('Custom');
      setFormTenantId('GLOBAL');
    } catch (err) {
      setAddError(err.message || 'Failed to add known app');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (id, tenantId) => {
    setRemoving(true);
    try {
      await removeKnownApp(id, tenantId);
      setApps((prev) => prev.filter((a) => !(a.appId === id && a.tenantId === tenantId)));
      setConfirmRemove(null);
    } catch (err) {
      setError(err.message || 'Failed to remove known app');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <CubeIcon className="h-7 w-7 text-blue-500" />
          <h1 className="text-2xl font-bold text-gray-100">Known Enterprise Apps</h1>
        </div>
        <p className="text-sm text-gray-500">
          Enterprise applications that are expected in M365 audit logs.
          These are visually marked as &quot;known&quot; in investigation results.
        </p>
      </div>

      {/* Scope Selector */}
      <div className="flex gap-2 mb-6">
        {[
          { key: 'ALL', label: 'All' },
          { key: 'GLOBAL', label: 'Global' },
          { key: 'TENANT', label: 'Tenant-Specific' },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setScope(key)}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              scope === key
                ? 'bg-gray-700 text-gray-100 shadow-sm'
                : 'text-gray-500 hover:text-gray-300 bg-gray-900 border border-gray-800'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Add Form */}
      <div className="card mb-6">
        <h2 className="text-sm font-semibold text-gray-300 mb-4">Add Known App</h2>
        <form onSubmit={handleAdd} className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <input
                type="text"
                placeholder="App ID (GUID)"
                value={appId}
                onChange={(e) => setAppId(e.target.value)}
                className="input-field font-mono text-xs"
                required
              />
            </div>
            <div className="flex-1">
              <input
                type="text"
                placeholder="Display Name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="input-field"
                required
              />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="w-full sm:w-40">
              <select
                value={formCategory}
                onChange={(e) => setFormCategory(e.target.value)}
                className="input-field"
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <input
                type="text"
                placeholder="Tenant ID (leave GLOBAL for all tenants)"
                value={formTenantId}
                onChange={(e) => setFormTenantId(e.target.value)}
                className="input-field font-mono text-xs"
              />
            </div>
            <button
              type="submit"
              disabled={adding || !appId.trim() || !displayName.trim()}
              className="btn-primary flex items-center gap-2 whitespace-nowrap"
            >
              {adding ? (
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
              ) : (
                <PlusIcon className="h-4 w-4" />
              )}
              Add
            </button>
          </div>
        </form>
        {addError && (
          <p className="mt-2 text-sm text-red-400">{addError}</p>
        )}
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-4 p-4 bg-red-900/30 border border-red-700/50 rounded-lg text-red-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
          <button
            onClick={loadApps}
            className="ml-auto underline hover:no-underline text-red-400"
          >
            Retry
          </button>
        </div>
      )}

      {/* Filter and Count */}
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <input
            type="text"
            placeholder="Filter by app ID, name, category..."
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
            className="input-field pl-9"
          />
        </div>
        <span className="text-xs text-gray-500">
          {filteredApps.length} known app{filteredApps.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table
            ref={tableRef}
            className="text-sm"
            style={{ tableLayout: 'fixed', width: `${colWidths.reduce((a, b) => a + b, 0)}px` }}
          >
            <thead>
              <tr className="border-b border-gray-800">
                {['App ID', 'Display Name', 'Category', 'Scope', 'Added By', 'Date', 'Actions'].map((label, colIdx) => (
                  <th
                    key={label}
                    ref={(el) => { thRefs.current[colIdx] = el; }}
                    className={`${colIdx === 6 ? 'text-center' : 'text-left'} text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap relative select-none`}
                    style={{ width: `${colWidths[colIdx]}px` }}
                  >
                    <span className="truncate">{label}</span>
                    <div
                      className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group"
                      onMouseDown={(e) => startResize(e, colIdx)}
                    >
                      <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {loading && (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <svg
                        className="animate-spin h-8 w-8 text-blue-500"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                      </svg>
                      <span className="text-gray-500 text-sm">Loading known apps...</span>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && filteredApps.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <CubeIcon className="h-10 w-10 text-gray-700 mx-auto mb-3" />
                    <p className="text-gray-400 font-medium">No known apps found</p>
                    <p className="text-gray-600 text-sm mt-1">
                      {filterQuery
                        ? 'Try adjusting your search query.'
                        : 'Add a known app above to get started.'}
                    </p>
                  </td>
                </tr>
              )}

              {!loading &&
                pagedApps.map((app) => {
                  const key = `${app.appId}-${app.tenantId}`;
                  return (
                    <tr
                      key={key}
                      className="hover:bg-gray-800/50 transition-colors"
                    >
                      <td className="px-4 py-3 font-mono text-xs text-gray-200 truncate overflow-hidden" title={app.appId}>
                        {app.appId}
                      </td>
                      <td className="px-4 py-3 text-gray-300 truncate overflow-hidden" title={app.displayName}>
                        {app.displayName}
                      </td>
                      <td className="px-4 py-3">
                        <Badge tone={appCategoryTone(app.category)} dot>{app.category}</Badge>
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs font-mono truncate overflow-hidden" title={app.tenantId}>
                        {app.tenantId === 'GLOBAL' ? (
                          <span className="text-blue-400">Global</span>
                        ) : (
                          app.tenantId
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs truncate overflow-hidden" title={app.addedBy || ''}>
                        {app.addedBy || '\u2014'}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs truncate overflow-hidden">
                        {formatDate(app.addedDate)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {confirmRemove === key ? (
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => handleRemove(app.appId, app.tenantId)}
                              disabled={removing}
                              className="btn-danger px-2 py-1 text-xs"
                            >
                              {removing ? 'Removing...' : 'Confirm'}
                            </button>
                            <button
                              onClick={() => setConfirmRemove(null)}
                              className="btn-secondary px-2 py-1 text-xs"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setConfirmRemove(key)}
                            className="text-gray-600 hover:text-red-400 transition-colors p-1"
                            title="Remove known app"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
        {!loading && filteredApps.length > 0 && (
          <Pagination
            currentPage={safeCurrentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={filteredApps.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
          />
        )}
      </div>
    </div>
  );
}

